/**
 * "Delete service…" (editors only), the one dialog for it: Apps › a service's "⋯" and the project Home's row "⋯". It
 * shows what goes with the service, what in the other services still uses it, and asks for the service's name typed to confirm. vfs-service does the whole delete as one operation (services/ServiceDeleteService:
 * it stops the service, removes its folder as one commit and cleans its pins, default app, secrets and jobs) and keeps it
 * so it can be undone. Data stores are kept. A refusal or a failure is shown here, the dialog stays open.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, message as toast } from 'antd';
import { deleteService, getDeletePlan, restoreDeletedService } from '../../services/ServiceDeleteService';
import { openHistory } from '../../utils/workspaceModel';

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** The lines of "Also removed": only what this service has. */
export function cleanupLines(cleanup) {
  if (!cleanup) { return []; }
  const config = [
    cleanup.vars?.length ? plural(cleanup.vars.length, 'variable') : null,
    cleanup.secrets?.length ? plural(cleanup.secrets.length, 'secret') : null,
    cleanup.jobs?.length ? plural(cleanup.jobs.length, 'scheduled job') : null,
  ].filter(Boolean);
  return [
    cleanup.pins?.length ? `Its pinned ${cleanup.pins.length === 1 ? 'file' : 'files'} (${cleanup.pins.map((pin) => pin.split('/').pop()).join(', ')})` : null,
    cleanup.defaultApp ? 'The default app choice (it is this service)' : null,
    config.length ? `Its ${config.join(', ')}` : null,
    cleanup.repoFlags ? 'Its Read-only / Context only flags' : null,
  ].filter(Boolean);
}

/**
 * The message after a delete (`done`: deleteService's answer), the same wherever it was deleted from: "<name> was
 * deleted.", how many places still use it, Undo (puts it back; `onRestored(done)` after) and See in History (Activity).
 */
export function showDeletedMessage(projectId, done, { onRestored = null } = {}) {
  const key = `service-deleted-${done.id}`;
  const left = done.references?.length || 0;
  const undo = async() => {
    toast.destroy(key);
    try {
      await restoreDeletedService(projectId, done.id);
      onRestored?.(done);
      toast.success(`${done.service} is back`);
    } catch (error) {
      toast.error(error?.message || `${done.service} could not be restored`);
    }
  };
  toast.open({
    key,
    type: 'success',
    duration: 10,
    content: (
      <span data-testid="apps-deleted-toast">
        {`${done.service} was deleted.`}{left ? ` ${left} ${left === 1 ? 'place still uses' : 'places still use'} it.` : ''}{' '}
        <button className="apps-info-link" data-testid="apps-deleted-undo" onClick={undo} type="button">Undo</button>
        {' · '}
        <button className="apps-info-link" data-testid="apps-deleted-history" onClick={() => { toast.destroy(key); openHistory({ tab: 'activity' }); }} type="button">See in History</button>
      </span>
    ),
  });
}

/** "services/web/src/api.js:3" */
const whereOf = (ref) => `${ref.path}${ref.line ? `:${ref.line}` : ''}`;

export default function DeleteServiceDialog({ projectId, service, open, onClose, onDeleted }) {
  const name = String(service || '').replace(/^services\//, '');
  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !name) { return undefined; }
    let live = true;
    setPlan(null); setPlanError(null); setTyped(''); setError(null);
    getDeletePlan(projectId, name)
      .then((found) => { if (live) { setPlan(found); } })
      .catch((e) => { if (live) { setPlanError(e?.message || 'Could not read what uses this service.'); } });
    return () => { live = false; };
  }, [open, projectId, name]);

  const matches = typed.trim() === name;
  const remove = async() => {
    if (!matches || busy) { return; }
    setBusy(true);
    setError(null);
    try {
      const done = await deleteService(projectId, name, { confirm: typed.trim() });
      onDeleted?.(done);
    } catch (e) {
      setError(e?.message || `${name} could not be deleted.`);
    } finally {
      setBusy(false);
    }
  };

  const lines = cleanupLines(plan?.cleanup);
  const references = plan?.references || [];
  return (
    <Modal
      cancelButtonProps={{ disabled: busy }}
      cancelText="Cancel"
      confirmLoading={busy}
      okButtonProps={{ danger: true, disabled: !matches, 'data-testid': 'delete-service-confirm' }}
      okText="Delete service"
      onCancel={() => { if (!busy) { onClose(); } }}
      onOk={remove}
      open={open}
      title={`Delete ${name}`}
    >
      <div data-testid="delete-service-dialog">
        <p>This removes <code>services/{name}</code> and stops it. You can undo it from History.</p>
        {!plan && !planError && <p className="apps-muted" data-testid="delete-service-loading">Checking what uses it…</p>}
        {planError && <p className="apps-muted" data-testid="delete-service-plan-error">{planError}</p>}
        {lines.length > 0 && (
          <>
            <p style={{ marginBottom: 4 }}>Also removed:</p>
            <ul data-testid="delete-service-cleanup">{lines.map((line) => <li key={line}>{line}</li>)}</ul>
          </>
        )}
        {references.length > 0 && (
          <div className="apps-problem" data-testid="delete-service-references" role="note">
            <strong>{`Still used in ${plural(references.length, 'place')}${plan.referencesTruncated ? ' or more' : ''}`}</strong>
            <span className="apps-problem-text">These will fail until you change them:</span>
            <ul>
              {references.map((ref) => (
                <li key={`${whereOf(ref)}:${ref.kind}`}><code>{whereOf(ref)}</code>{ref.text ? ` · ${ref.text}` : ''}</li>
              ))}
            </ul>
          </div>
        )}
        {plan && <p className="apps-muted" data-testid="delete-service-data">Data stores are kept: their data is shared by the project&apos;s services.</p>}
        <label htmlFor="delete-service-name" style={{ display: 'block', marginTop: 8 }}>
          Type <strong>{name}</strong> to confirm
        </label>
        <input
          autoComplete="off"
          className="ant-input"
          data-testid="delete-service-name"
          disabled={busy}
          id="delete-service-name"
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); remove(); } }}
          type="text"
          value={typed}
        />
        {error && <p className="apps-bad" data-testid="delete-service-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

DeleteServiceDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func,
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.string,
  // `services/<name>` or the short name.
  service: PropTypes.string,
};
